import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { range } from "../../../utils/lodash";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import useStyles from "./StarRatingRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import Button from "../../shared/Button";

interface Props extends RendererProps {
    containerRef: React.LegacyRef<HTMLDivElement>;
    setValue(value: number): void;
    starWidth?: number;
    starHeight?: number;
    value: number;
    split: number;
    maxValue: number;
    elementId: string;
}

function getStars(props: Props) {
    const starCount = props.maxValue * (props.split || 1);
    return range(starCount);
}

export default function StarRatingRenderer(props: Props) {
    const [hoveredStarValue, setHoveredStarValue] = React.useState(0);
    const { value, setValue, elementId, split = 1, starWidth = 24, starHeight = 24 } = props;
    const splitWidth = starWidth / split;

    const theme = useTheme();
    const classes = useStyles({ theme, starWidth, starHeight, splitWidth });

    return (
        <div
            id={elementId}
            className={classnames(classes.starRating)}
            onMouseOut={() => setHoveredStarValue(0)}
        >
            <div className="flex" />
            {getStars(props).map((index) => {
                const starValue = (index + 1) / split;
                const isIconHighlighted =
                    hoveredStarValue > 0 ? starValue <= hoveredStarValue : starValue <= value;

                const spacer = (
                    <div
                        className={classnames(classes.starRatingSpacer)}
                        onClick={() => setValue(starValue)}
                        onMouseOver={() => setHoveredStarValue(starValue)}
                    />
                );

                return (
                    <React.Fragment key={index}>
                        {index % split === 0 && spacer}
                        <div
                            onClick={() => setValue(starValue)}
                            onMouseOver={() => setHoveredStarValue(starValue)}
                            className={classnames(classes.starRatingStar)}
                        >
                            <FontAwesomeIcon
                                style={{
                                    position: "absolute",
                                    width: starWidth,
                                    height: starHeight,
                                    left: index % split === 0 ? 0 : undefined,
                                    right: index % split === 1 ? 0 : undefined,
                                }}
                                icon={[isIconHighlighted ? "fas" : "far", "star"]}
                            />
                        </div>
                        {index % split === split - 1 && spacer}
                    </React.Fragment>
                );
            })}
            <div className="flex" />
            <Button icon={["fas", "times"]} size={16} onClick={() => setValue(null)} />
        </div>
    );
}
